import { Component, Inject, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PickingsService } from '@app/services/pickings/picking.service';
import { Store } from '@ngrx/store';
import { selectMeState } from '@app/core/store/core.selectors';
import { first, skipWhile } from 'rxjs/operators';
import { ProductsService } from '@app/services/products/product.service';
import { readSerialFailure, readSerialProduct, SerialRefusal, SerialStockRow } from '../serial-form/serial-form.component';

export interface StorageOverrideDialogData {
  prdID: number;
  storageStructureID: number;
  storagePath: string;
  currentOnHand: number;
  prdNameEn: string;
  // Known by the opener: skips the product fetch (patterns are fetched only for serialized products).
  prdIsSerialized?: number;
  serialPatterns?: string[];
}

@Component({
  selector: 'app-storage-override-dialog',
  templateUrl: './storage-override-dialog.component.html',
  styleUrls: ['./storage-override-dialog.component.scss']
})
export class StorageOverrideDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('mainInput') mainInput: ElementRef<HTMLInputElement>;
  form: FormGroup;
  isSubmitting: boolean = false;
  // Serialized products: the difference to the current on hand decides which serials are chosen or entered.
  // null until the product is known: nothing can be submitted before that.
  isSerialized: boolean | null = null;
  patterns: string[] = [];
  stock: SerialStockRow[] | null = null;
  stockState: 'loading' | 'ready' | 'error' = 'loading';
  serials: string[] = [];
  blocked = false;
  refused: SerialRefusal[] = [];
  failed = false;
  stale = false;
  staleOnHand: number | null = null;
  private usrID: number;
  private fTypeID: number;
  private locID: number;

  constructor(
    public dialogRef: MatDialogRef<StorageOverrideDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: StorageOverrideDialogData,
    private fb: FormBuilder,
    private pickingsService: PickingsService,
    private productsService: ProductsService,
    private store$: Store
  ) {
    this.form = this.fb.group({
      newOnHand: [data.currentOnHand, [Validators.required]],
      reason: ['']
    });
  }

  ngOnInit(): void {
    this.store$.select(selectMeState).pipe(
      skipWhile(me => me == null),
      first()
    ).subscribe((me: any) => {
      this.usrID = me.usrID;
      this.fTypeID = me.usrFTypeID;
      this.locID = me.usrLocID;
    });

    this.initProduct();
  }

  productError = false;

  private initProduct(): void {
    const known = this.data.prdIsSerialized;
    if (known != null && (Number(known) !== 1 || this.data.serialPatterns)) {
      this.applyProduct(Number(known) === 1, this.data.serialPatterns || []);
      return;
    }
    this.loadProduct();
  }

  loadProduct(): void {
    this.productError = false;
    this.productsService.getProduct(this.data.prdID).pipe(first()).subscribe({
      next: (res) => {
        const info = readSerialProduct(res);
        this.applyProduct(info.serialized, info.patterns);
      },
      error: () => {
        this.productError = true;
      }
    });
  }

  private applyProduct(serialized: boolean, patterns: string[]): void {
    this.patterns = patterns;
    this.isSerialized = serialized;
    if (serialized) {
      this.form.get('newOnHand').addValidators(Validators.min(0));
      this.form.get('newOnHand').updateValueAndValidity();
      this.loadStock();
    }
  }

  loadStock(): void {
    this.stockState = 'loading';
    this.pickingsService.getAllSerialsInStock(this.data.prdID, this.data.storageStructureID).pipe(first()).subscribe({
      next: (rows) => {
        this.stock = rows.map(r => ({ serialNumber: r.serialNumber, receivedAt: r.receivedAt }));
        this.stockState = 'ready';
      },
      error: () => {
        this.stockState = 'error';
      }
    });
  }

  get invalidQuantity(): boolean {
    const value = this.form.value.newOnHand;
    return value === null || value === '' || !Number.isInteger(Number(value)) || Number(value) < 0;
  }

  // New on hand minus the current on hand; 0 while the field is empty or invalid.
  get delta(): number {
    return this.invalidQuantity ? 0 : Number(this.form.value.newOnHand) - Number(this.data.currentOnHand);
  }

  get absDelta(): number {
    return Math.abs(this.delta);
  }

  // Units on hand that have no serial recorded in this storage.
  get unrecorded(): number {
    return this.stockState === 'ready' && this.stock ? Number(this.data.currentOnHand) - this.stock.length : 0;
  }

  get canSubmit(): boolean {
    if (this.isSubmitting || this.form.invalid || this.isSerialized === null) {
      return false;
    }
    if (!this.isSerialized) {
      return true;
    }
    return this.delta !== 0 && this.serials.length === this.absDelta && !this.blocked && (this.delta > 0 || this.stockState === 'ready');
  }

  onEnter(event: Event): void {
    // In the serial block Enter belongs to the entry field, not to Confirm.
    if (!this.isSerialized) {
      this.onSubmit();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.mainInput) {
        this.mainInput.nativeElement.focus();
        this.mainInput.nativeElement.select();
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onSubmit(): void {
    if (!this.canSubmit) {
      return;
    }

    this.isSubmitting = true;
    this.failed = false;
    this.stale = false;

    const overrideData = {
      prdID: this.data.prdID,
      storageStructureID: this.data.storageStructureID,
      storagePath: this.data.storagePath,
      quantity: this.form.value.newOnHand,
      usrID: this.usrID,
      fTypeID: this.fTypeID,
      locationID: this.locID
    } as any;
    if (this.isSerialized) {
      // Delta rule: serials in minus serials out equals new on hand minus current on hand.
      overrideData.serialsIn = this.delta > 0 ? this.serials : [];
      overrideData.serialsOut = this.delta < 0 ? this.serials : [];
    }

    this.pickingsService.onOverride(overrideData as any).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.dialogRef.close(true);
      },
      error: (error) => {
        console.error('Error performing override:', error);
        this.isSubmitting = false;
        if (this.isSerialized) {
          const failure = readSerialFailure(error);
          this.refused = failure.refused;
          this.failed = failure.failed;
          this.stale = !!failure.mismatch;
          this.staleOnHand = failure.onHand ?? null;
        }
      }
    });
  }
}
